import { useSearchParams } from 'react-router-dom';
import { LayoutDashboard, Users, Rss, Layers, ScrollText, ShieldCheck, Tags } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { AdminOverview } from './AdminOverview';
import { AdminUsers } from './AdminUsers';
import { AdminFeeds } from './AdminFeeds';
import { AdminReleaseTypes } from './AdminReleaseTypes';
import { AdminTags } from './AdminTags';
import { AdminLogs } from './serverLogs/AdminLogs';

type AdminTab = 'overview' | 'users' | 'feeds' | 'tags' | 'release-types' | 'logs';

const tabs: { id: AdminTab; label: string; icon: typeof Users }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'feeds', label: 'Feeds', icon: Rss },
  { id: 'tags', label: 'Tags', icon: Tags },
  { id: 'release-types', label: 'Release Types', icon: Layers },
  { id: 'logs', label: 'Logs', icon: ScrollText },
];

const isAdminTab = (value: string | null): value is AdminTab => tabs.some((t) => t.id === value);

export function AdminPanel() {
  // The tab lives in the URL (?tab=logs&…) so a toast can deep-link into the Logs tab and a
  // reload keeps the view. Switching tabs replaces the whole query, dropping the Logs filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: AdminTab = isAdminTab(requestedTab) ? requestedTab : 'overview';
  const setActiveTab = (tab: AdminTab) => setSearchParams({ tab }, { replace: true });

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Admin</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage users, roles, RSS feeds, tags, and read server logs.</p>
        </div>
      </div>

      <div className="border-b border-border">
        <nav className="-mb-px flex gap-6 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                aria-current={active ? 'page' : undefined}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex shrink-0 items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'overview' && <AdminOverview />}
      {activeTab === 'users' && <AdminUsers />}
      {activeTab === 'feeds' && <AdminFeeds />}
      {activeTab === 'tags' && <AdminTags />}
      {activeTab === 'release-types' && <AdminReleaseTypes />}
      {activeTab === 'logs' && <AdminLogs />}
    </div>
  );
}
